"use client";
export const dynamic = "force-dynamic";

import Link from "next/link";
import { motion } from "framer-motion";
import {
  Sparkles,
  Rocket,
  Bug,
  Wrench,
  Zap,
  ShieldCheck,
  Bell,
  ArrowLeft,
  Rss,
  Tag,
  Calendar,
  CheckCircle2,
  Star,
} from "lucide-react";
import BrandMark from "@/components/BrandMark";
import ThemeToggle from "@/components/ThemeToggle";

type EntryType = "feature" | "improvement" | "fix" | "security" | "performance";

const TYPE_META: Record<
  EntryType,
  { label: string; icon: React.ElementType; color: string; bg: string; border: string }
> = {
  feature: {
    label: "ویژگی جدید",
    icon: Sparkles,
    color: "text-primary-main",
    bg: "bg-primary-main/10",
    border: "border-primary-main/30",
  },
  improvement: {
    label: "بهبود",
    icon: Wrench,
    color: "text-secondary-main",
    bg: "bg-secondary-main/10",
    border: "border-secondary-main/30",
  },
  fix: {
    label: "رفع باگ",
    icon: Bug,
    color: "text-orange-400",
    bg: "bg-orange-400/10",
    border: "border-orange-400/30",
  },
  security: {
    label: "امنیت",
    icon: ShieldCheck,
    color: "text-emerald-400",
    bg: "bg-emerald-400/10",
    border: "border-emerald-400/30",
  },
  performance: {
    label: "سرعت",
    icon: Zap,
    color: "text-purple-400",
    bg: "bg-purple-400/10",
    border: "border-purple-400/30",
  },
};

type Entry = {
  version: string;
  date: string;
  title: string;
  highlight?: boolean;
  tag?: string;
  summary: string;
  changes: { type: EntryType; text: string }[];
};

const ENTRIES: Entry[] = [
  {
    version: "v۲٫۴٫۰",
    date: "۱۸ اردیبهشت ۱۴۰۵",
    title: "موتور پاسخ خودکار نسل جدید",
    highlight: true,
    tag: "انتشار بزرگ",
    summary:
      "بازنویسی کامل موتور پاسخ خودکار با هوش مصنوعی فارسی بومی، پنج برابر سریع‌تر و با دقت ۹۸٪ در تشخیص نیت کاربر.",
    changes: [
      { type: "feature", text: "موتور NLU فارسی نسل جدید با پشتیبانی از گویش‌های محلی" },
      { type: "feature", text: "ساخت پاسخ خودکار با درگ‌اند‌دراپ بصری" },
      { type: "performance", text: "کاهش ۸۰٪ زمان پاسخ‌دهی پیام‌های دایرکت" },
      { type: "improvement", text: "بازطراحی کامل داشبورد آمار و تحلیل" },
    ],
  },
  {
    version: "v۲٫۳٫۲",
    date: "۱۰ اردیبهشت ۱۴۰۵",
    title: "بهبود پایداری و امنیت",
    summary: "رفع چند مشکل گزارش‌شده توسط کاربران و افزایش استانداردهای امنیتی.",
    changes: [
      { type: "security", text: "ارتقاء پروتکل احراز هویت دومرحله‌ای (2FA)" },
      { type: "fix", text: "رفع مشکل قطع شدن گاه‌به‌گاه اتصال اکانت اینستاگرام" },
      { type: "fix", text: "اصلاح نمایش نمودار آمار در حالت تم روشن" },
      { type: "improvement", text: "بهبود پیام‌های خطا با راهنمایی دقیق‌تر" },
    ],
  },
  {
    version: "v۲٫۳٫۰",
    date: "۲۸ فروردین ۱۴۰۵",
    title: "صندوق پیام‌های یکپارچه",
    tag: "جدید",
    summary:
      "تمام پیام‌های دایرکت، کامنت و منشن در یک صندوق متمرکز با فیلتر هوشمند و برچسب‌گذاری خودکار.",
    changes: [
      { type: "feature", text: "صندوق یکپارچه با گروه‌بندی هوشمند مکالمات" },
      { type: "feature", text: "برچسب‌گذاری خودکار پیام‌ها بر اساس موضوع" },
      { type: "improvement", text: "نمایش پیش‌نمایش رسانه‌ها در فهرست پیام‌ها" },
      { type: "performance", text: "بارگذاری Lazy برای مکالمات با حجم بالا" },
    ],
  },
  {
    version: "v۲٫۲٫۱",
    date: "۱۵ فروردین ۱۴۰۵",
    title: "حالت تاریک و تجربه موبایل",
    summary: "پشتیبانی کامل از تم تیره و بهینه‌سازی کامل واسط کاربری برای دستگاه‌های موبایل.",
    changes: [
      { type: "feature", text: "تم تاریک با تنظیم خودکار بر اساس سیستم" },
      { type: "improvement", text: "بازطراحی کامل داشبورد برای موبایل" },
      { type: "fix", text: "رفع مشکل اسکرول صفحه در iOS Safari" },
    ],
  },
  {
    version: "v۲٫۲٫۰",
    date: "۱ فروردین ۱۴۰۵",
    title: "آغاز سال نو با چهره‌ای تازه",
    highlight: true,
    tag: "بازطراحی",
    summary:
      "بازطراحی کامل واسط کاربری با تمرکز بر سرعت، سادگی و حس بومی فارسی. تجربه‌ای کاملاً نو.",
    changes: [
      { type: "feature", text: "سیستم طراحی جدید با تایپوگرافی اختصاصی فارسی" },
      { type: "feature", text: "نمودارها و اینفوگرافیک‌های تعاملی جدید" },
      { type: "improvement", text: "نوار کناری جمع‌شونده با میانبرهای سریع" },
      { type: "performance", text: "افزایش ۴۰٪ سرعت بارگذاری اولیه داشبورد" },
    ],
  },
];

export default function ChangelogPage() {
  return (
    <div
      dir="rtl"
      className="min-h-screen bg-white text-grayScale-900 dark:bg-grayScale-900 dark:text-white selection:bg-primary-main selection:text-white"
    >
      {/* Ambient background */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <motion.div
          animate={{ scale: [1, 1.15, 1] }}
          transition={{ duration: 12, repeat: Infinity }}
          className="absolute -right-40 top-0 h-[600px] w-[600px] rounded-full bg-primary-main/10 dark:bg-primary-main/15 blur-[140px]"
        />
        <motion.div
          animate={{ scale: [1.15, 1, 1.15] }}
          transition={{ duration: 14, repeat: Infinity }}
          className="absolute -left-40 top-1/3 h-[600px] w-[600px] rounded-full bg-secondary-main/10 blur-[140px]"
        />
        <div
          className="absolute inset-0 opacity-[0.04] dark:opacity-[0.05]"
          style={{
            backgroundImage:
              "linear-gradient(currentColor 1px, transparent 1px), linear-gradient(90deg, currentColor 1px, transparent 1px)",
            backgroundSize: "60px 60px",
            maskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
          }}
        />
      </div>

      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-grayScale-200 dark:border-white/[0.06] bg-white/85 dark:bg-grayScale-900/85 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 px-6">
          <Link href="/" className="flex items-center gap-3">
            <BrandMark size={36} />
            <div className="hidden flex-col leading-none sm:flex">
              <span className="text-[14px] font-black tracking-tight">ترندی اسمارت</span>
              <span className="mt-1 text-[9px] font-bold tracking-[0.25em] text-primary-main">
                TRENDI · SMART
              </span>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <ThemeToggle variant="inline" />
            <Link
              href="/"
              className="group inline-flex items-center gap-1.5 rounded-lg bg-grayScale-100 dark:bg-white/[0.06] px-3 py-2 text-[13px] font-bold text-grayScale-700 dark:text-grayScale-200 transition-colors hover:bg-grayScale-200 dark:hover:bg-white/[0.1]"
            >
              <ArrowLeft size={14} className="transition-transform group-hover:-translate-x-0.5" />
              <span>بازگشت به خانه</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 mx-auto max-w-6xl px-6 pt-16 pb-12 sm:pt-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-grayScale-200 dark:border-white/10 bg-grayScale-50 dark:bg-white/[0.04] px-4 py-2 backdrop-blur-sm">
            <Rss size={14} className="text-primary-main" />
            <span className="text-[12px] font-bold text-grayScale-700 dark:text-grayScale-300">
              تاریخچه به‌روزرسانی‌های محصول
            </span>
          </div>
          <h1 className="mb-5 text-4xl font-black leading-[1.15] tracking-tight sm:text-5xl lg:text-6xl">
            تغییرات و<span className="brand-gradient-text"> به‌روزرسانی‌ها</span>
          </h1>
          <p className="mx-auto max-w-2xl text-base leading-relaxed text-grayScale-600 dark:text-grayScale-400 sm:text-lg">
            هر هفته ترندی اسمارت سریع‌تر، هوشمندتر و قدرتمندتر می‌شود. اینجا می‌توانید همهٔ
            ویژگی‌های جدید، بهبودها و رفع باگ‌ها را به ترتیب زمانی دنبال کنید.
          </p>

          {/* Subscribe band */}
          <div className="mx-auto mt-8 flex max-w-xl flex-col items-center gap-3 rounded-2xl border border-grayScale-200 dark:border-white/10 bg-grayScale-50 dark:bg-white/[0.04] p-4 backdrop-blur-sm sm:flex-row sm:p-3">
            <div className="flex flex-1 items-center gap-3 pr-2">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary-main/15 text-primary-main">
                <Bell size={18} />
              </div>
              <div className="text-right">
                <p className="text-[13px] font-bold text-grayScale-900 dark:text-white">
                  عضویت در خبرنامه نسخه‌ها
                </p>
                <p className="text-[11px] text-grayScale-600 dark:text-grayScale-400">
                  هر آپدیت مهم را در ایمیل دریافت کنید
                </p>
              </div>
            </div>
            <Link
              href="/auth"
              className="flex w-full items-center justify-center gap-1.5 rounded-xl bg-primary-main px-5 py-2.5 text-[13px] font-black text-white shadow-lg shadow-primary-main/30 transition-colors hover:bg-primary-400 sm:w-auto"
            >
              عضویت رایگان
              <ArrowLeft size={14} />
            </Link>
          </div>
        </motion.div>
      </section>

      {/* Filter chips */}
      <section className="relative z-10 mx-auto max-w-6xl px-6 pb-10">
        <div className="flex flex-wrap items-center justify-center gap-2">
          {(["feature", "improvement", "performance", "fix", "security"] as EntryType[]).map(
            (t) => {
              const meta = TYPE_META[t];
              const Icon = meta.icon;
              return (
                <span
                  key={t}
                  className={`inline-flex items-center gap-1.5 rounded-full border ${meta.border} ${meta.bg} px-3 py-1.5 text-[11px] font-bold ${meta.color}`}
                >
                  <Icon size={12} /> {meta.label}
                </span>
              );
            },
          )}
        </div>
      </section>

      {/* Timeline */}
      <section className="relative z-10 mx-auto max-w-4xl px-6 pb-24">
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute right-4 top-2 bottom-2 w-px bg-gradient-to-b from-primary-main/40 via-grayScale-200 dark:via-white/10 to-transparent sm:right-6" />

          <div className="space-y-8">
            {ENTRIES.map((e, idx) => (
              <motion.article
                key={e.version}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ delay: Math.min(idx * 0.06, 0.3), duration: 0.5 }}
                className="relative pr-12 sm:pr-16"
              >
                {/* Timeline dot */}
                <div className="absolute right-0 top-3 flex h-9 w-9 items-center justify-center rounded-full border border-grayScale-200 dark:border-white/10 bg-white dark:bg-grayScale-900 sm:h-12 sm:w-12">
                  <div
                    className={`h-3 w-3 rounded-full sm:h-3.5 sm:w-3.5 ${
                      e.highlight
                        ? "bg-primary-main shadow-lg shadow-primary-main/50"
                        : "bg-grayScale-300 dark:bg-white/30"
                    }`}
                  />
                  {e.highlight && (
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary-main/30" />
                  )}
                </div>

                <div
                  className={`group relative overflow-hidden rounded-2xl border ${
                    e.highlight
                      ? "border-primary-main/30 bg-gradient-to-br from-primary-main/[0.06] to-secondary-main/[0.04] dark:from-primary-main/[0.08]"
                      : "border-grayScale-200 dark:border-white/[0.08] bg-white dark:bg-white/[0.03]"
                  } p-5 backdrop-blur-sm transition-colors hover:border-primary-main/40 dark:hover:border-white/20 sm:p-7`}
                >
                  {e.highlight && (
                    <div className="pointer-events-none absolute -top-20 -left-20 h-40 w-40 rounded-full bg-primary-main/20 blur-3xl" />
                  )}

                  {/* Meta row */}
                  <div className="mb-4 flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-grayScale-100 dark:bg-white/[0.06] px-2.5 py-1 text-[11px] font-bold text-grayScale-900 dark:text-white">
                      <Tag size={11} className="text-primary-main" />
                      {e.version}
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-grayScale-600 dark:text-grayScale-400">
                      <Calendar size={11} />
                      {e.date}
                    </span>
                    {e.tag && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-secondary-main/40 bg-secondary-main/10 px-2.5 py-1 text-[10px] font-black text-secondary-main">
                        <Star size={10} fill="currentColor" />
                        {e.tag}
                      </span>
                    )}
                  </div>

                  {/* Title */}
                  <h2 className="mb-2 text-xl font-black leading-tight tracking-tight text-grayScale-900 dark:text-white sm:text-2xl">
                    {e.title}
                  </h2>
                  <p className="mb-5 text-[13.5px] leading-relaxed text-grayScale-700 dark:text-grayScale-300 sm:text-sm">
                    {e.summary}
                  </p>

                  {/* Changes */}
                  <ul className="space-y-2.5">
                    {e.changes.map((c, i) => {
                      const meta = TYPE_META[c.type];
                      const Icon = meta.icon;
                      return (
                        <li
                          key={i}
                          className="flex items-start gap-3 rounded-xl border border-grayScale-200 dark:border-white/[0.05] bg-grayScale-50 dark:bg-white/[0.02] p-3 transition-colors hover:bg-grayScale-100 dark:hover:bg-white/[0.04]"
                        >
                          <span
                            className={`mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg ${meta.bg} ${meta.color}`}
                          >
                            <Icon size={13} />
                          </span>
                          <div className="flex-1">
                            <span
                              className={`mb-1 inline-block rounded-md px-1.5 py-0.5 text-[9.5px] font-black uppercase tracking-wider ${meta.bg} ${meta.color}`}
                            >
                              {meta.label}
                            </span>
                            <p className="text-[13px] leading-relaxed text-grayScale-800 dark:text-grayScale-200">
                              {c.text}
                            </p>
                          </div>
                          <CheckCircle2
                            size={14}
                            className="mt-1 flex-shrink-0 text-grayScale-400 dark:text-grayScale-600"
                          />
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </motion.article>
            ))}
          </div>

          {/* Footer note */}
          <div className="relative pr-12 pt-8 sm:pr-16">
            <div className="absolute right-0 top-8 flex h-9 w-9 items-center justify-center rounded-full border border-dashed border-grayScale-300 dark:border-white/15 bg-white dark:bg-grayScale-900 sm:h-12 sm:w-12">
              <Rocket size={14} className="text-grayScale-400 dark:text-grayScale-500" />
            </div>
            <div className="rounded-2xl border border-dashed border-grayScale-200 dark:border-white/10 bg-grayScale-50 dark:bg-white/[0.02] p-6 text-center">
              <p className="text-[13px] text-grayScale-600 dark:text-grayScale-400">
                این آغاز ماجراست. به‌زودی به‌روزرسانی‌های بزرگ‌تری در راه است.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
